import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    if (this.isDrawer) {
      this.#rebuildAllVariantSelectOptions();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  /**
   * Handles variant option change on a cart line item.
   * @param {number} line - The line item index.
   * @param {Event} event - The change event.
   */
  onLineItemVariantChange(line, event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return;

    const select = event.target;
    const previousValue = select.dataset.previousValue ?? select.defaultValue;

    this.#rebuildVariantSelectOptions(lineItemRow);

    const variantId = this.#resolveVariantId(lineItemRow);

    if (!variantId) {
      select.value = previousValue;
      this.#rebuildVariantSelectOptions(lineItemRow);
      this.#handleCartError(
        line,
        { errors: 'Selected variant is unavailable' },
        { select, previousValue }
      );
      return;
    }

    const currentVariantId = Number(lineItemRow.dataset.variantId);
    if (variantId === currentVariantId) return;

    const quantity = Number(lineItemRow.dataset.lineQuantity) || 1;

    this.updateLineItemVariant({
      line,
      id: variantId,
      quantity,
      select,
      previousValue,
    });
  }

  /**
   * Updates the variant for a cart line item.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.id - The new variant id.
   * @param {number} config.quantity - The line quantity.
   * @param {HTMLSelectElement} [config.select] - The select that triggered the change.
   * @param {string} [config.previousValue] - The prior select value for error recovery.
   */
  updateLineItemVariant(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('variant-change:user-action');

    this.#disableCartItems();

    const { line, id, quantity, select, previousValue } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // /cart/change cannot swap a line's variant: it only uses `line` or `id`
    // to find the line, and rejects a variant id there. So the old line is
    // removed and the new size added with the same properties (Final Sale
    // rides along as one, all the way to checkout). Removing first means the
    // cart validations (limit-one, the employee item limit) never see both
    // sizes at once. If the new size cannot be added, the original line is
    // put back as it was. A last update.js call returns the cart and sections
    // either way, so the drawer always shows what the cart really holds.
    const row = this.refs.cartItemRows[line - 1];
    const lineKey = row?.dataset.key;
    const oldVariantId = Number(row?.dataset.variantId);
    // Liquid's `json` writes item.properties as [[name, value], ...], while
    // /cart/add.js only accepts an object.
    let properties = {};
    try {
      const parsed = JSON.parse(row?.dataset.lineProperties || '{}');
      properties = Array.isArray(parsed) ? Object.fromEntries(parsed) : parsed || {};
    } catch (error) {
      properties = {};
    }

    /**
     * @param {string} url
     * @param {Object} payload
     */
    const post = (url, payload) =>
      fetch(url, fetchConfig('json', { body: JSON.stringify(payload) })).then((response) => response.json());
    /** @param {number} variantId */
    const add = (variantId) => post(Theme.routes.cart_add_url, { items: [{ id: variantId, quantity, properties }] });

    /** @type {string | null} */
    let failure = null;
    // Where the failure message goes once the drawer re-renders: the line
    // itself if nothing changed, the top line if the original was re-added.
    let failureLine = line;

    cartTotal?.shimmer();

    const removeOldSize = lineKey
      ? post(Theme.routes.cart_change_url, { id: lineKey, quantity: 0 })
      : Promise.resolve({ status: 404, description: 'Cart line not found' });

    removeOldSize
      .then(async (removed) => {
        if (removed.status) {
          failure = removed.description || removed.message;
          return;
        }

        const added = await add(id);
        if (added.status) {
          failure = added.description || added.message;
          failureLine = 1;
          const restored = await add(oldVariantId);
          if (restored.status) {
            // The original could not go back either (it may have just sold
            // out), so its line is gone: name it in the message, which lands
            // on whatever line is now first.
            const title = row?.dataset.productTitle || 'This item';
            let size = '';
            try {
              const variants = JSON.parse(row?.dataset.productVariants || '[]');
              size = variants.find((variant) => variant.id === oldVariantId)?.title ?? '';
            } catch (error) {
              size = '';
            }
            failure = `${title}${size ? ` (${size})` : ''} could not be put back in your bag: ${
              restored.description || restored.message
            }`;
          }
        }
      })
      .then(() =>
        post(Theme.routes.cart_update_url, {
          updates: {},
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        })
      )
      .then((parsedResponseText) => {
        resetShimmer(this);

        if (parsedResponseText.status) throw new Error(parsedResponseText.description || parsedResponseText.message);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText, { select, previousValue });
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');

        this.#updateCartQuantitySelectorButtonStates();
        this.#rebuildAllVariantSelectOptions();

        if (failure) {
          // After the morph, so wait for the refs to point at the new markup.
          // The re-render can leave no line to hang the message on (the
          // original could not be put back), so only show it where one exists.
          setTimeout(() => {
            const failedRow = this.refs.cartItemRows?.[failureLine - 1];
            const failedSelect = failedRow?.querySelector('select');
            if (failedRow) {
              // Pass the row's select so the handler keeps its value rather
              // than reaching for a quantity input the drawer may not have.
              this.#handleCartError(
                failureLine,
                { errors: failure },
                failedSelect ? { select: failedSelect, previousValue: failedSelect.value } : {}
              );
            } else {
              console.error(failure);
            }
          }, 100);
        }
      })
      .catch((error) => {
        console.error(error);
        if (select instanceof HTMLSelectElement && previousValue) {
          select.value = previousValue;
          const row = this.refs.cartItemRows?.[line - 1];
          if (row instanceof HTMLTableRowElement) {
            this.#rebuildVariantSelectOptions(row);
          }
        }
      })
      .finally(() => {
        // Also here: a network failure or a non-JSON answer (a 429 or 5xx
        // page) skips the success path, which would leave the total shimmering.
        resetShimmer(this);
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText, recovery = {}) => {
    const { select, previousValue } = recovery;

    if (select instanceof HTMLSelectElement) {
      select.value = previousValue ?? select.defaultValue;
    } else {
      const quantitySelector = this.refs.quantitySelectors[line - 1];
      const quantityInput = quantitySelector?.querySelector('input');

      if (quantityInput) {
        quantityInput.value = quantityInput.defaultValue;
      }
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
      this.#rebuildAllVariantSelectOptions();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Resolves the variant id from selected options on a cart line row.
   * @param {HTMLTableRowElement} row - The cart line row.
   * @returns {number | null} The matching variant id.
   */
  #resolveVariantId(row, requireAvailable = true) {
    const variants = this.#parseVariants(row);
    const optionCount = Number(row.dataset.optionCount) || 0;

    if (!variants.length || !optionCount) return null;

    const selectedOptions = this.#getSelectedOptions(row);
    const variant = this.#findVariant(variants, selectedOptions, optionCount, requireAvailable);

    return variant?.id ?? null;
  }

  /**
   * @param {HTMLTableRowElement} row
   * @returns {Array<{id: number, option1: string | null, option2: string | null, option3: string | null, available: boolean}>}
   */
  #parseVariants(row) {
    try {
      return JSON.parse(row.dataset.productVariants || '[]');
    } catch {
      return [];
    }
  }

  /**
   * @param {HTMLTableRowElement} row
   * @returns {Record<number, string>}
   */
  #getSelectedOptions(row) {
    /** @type {Record<number, string>} */
    const selectedOptions = {};

    if (row.dataset.colorValue != null && row.dataset.colorOptionIndex != null) {
      selectedOptions[Number(row.dataset.colorOptionIndex)] = row.dataset.colorValue;
    }

    row.querySelectorAll('.wf-cart-variant-select').forEach((element) => {
      if (!(element instanceof HTMLSelectElement)) return;
      selectedOptions[Number(element.dataset.optionIndex)] = element.value;
    });

    return selectedOptions;
  }

  /**
   * @param {Array<{id: number, option1: string | null, option2: string | null, option3: string | null, available: boolean}>} variants
   * @param {Record<number, string>} selectedOptions
   * @param {number} optionCount
   */
  #findVariant(variants, selectedOptions, optionCount, requireAvailable = false) {
    return variants.find((variant) => {
      if (requireAvailable && !variant.available) return false;

      const options = [variant.option1, variant.option2, variant.option3].slice(0, optionCount);

      return options.every((value, index) => selectedOptions[index] === value);
    });
  }

  /**
   * @param {Array<{id: number, option1: string | null, option2: string | null, option3: string | null, available: boolean}>} variants
   * @param {Record<number, string>} selectedOptions
   * @param {number} optionCount
   * @param {number} optionIndex
   * @returns {Set<string>}
   */
  #getAvailableValuesForOption(variants, selectedOptions, optionCount, optionIndex) {
    /** @type {Set<string>} */
    const values = new Set();

    for (const variant of variants) {
      if (!variant.available) continue;

      const options = [variant.option1, variant.option2, variant.option3].slice(0, optionCount);
      let matchesOthers = true;

      for (let i = 0; i < optionCount; i++) {
        if (i === optionIndex) continue;
        if (selectedOptions[i] !== options[i]) {
          matchesOthers = false;
          break;
        }
      }

      const value = options[optionIndex];
      if (matchesOthers && value) {
        values.add(value);
      }
    }

    return values;
  }

  #rebuildAllVariantSelectOptions() {
    // An empty cart renders no rows, so the ref is never set.
    for (const row of this.refs.cartItemRows ?? []) {
      if (!(row instanceof HTMLTableRowElement)) continue;
      if (!row.dataset.productVariants) continue;

      this.#rebuildVariantSelectOptions(row);
    }
  }

  /**
   * @param {HTMLTableRowElement} row
   */
  #rebuildVariantSelectOptions(row) {
    const variants = this.#parseVariants(row);
    const optionCount = Number(row.dataset.optionCount) || 0;

    if (!variants.length || !optionCount) return;

    const selectedOptions = this.#getSelectedOptions(row);

    row.querySelectorAll('.wf-cart-variant-select').forEach((element) => {
      if (!(element instanceof HTMLSelectElement)) return;

      const optionIndex = Number(element.dataset.optionIndex);
      const currentValue = element.value;
      const availableValues = this.#getAvailableValuesForOption(
        variants,
        selectedOptions,
        optionCount,
        optionIndex
      );

      availableValues.add(currentValue);

      /** @type {string[]} */
      const orderedValues = [];
      const seen = new Set();

      for (const variant of variants) {
        const options = [variant.option1, variant.option2, variant.option3].slice(0, optionCount);
        const value = options[optionIndex];

        if (value && availableValues.has(value) && !seen.has(value)) {
          seen.add(value);
          orderedValues.push(value);
        }
      }

      if (!seen.has(currentValue)) {
        orderedValues.unshift(currentValue);
      }

      element.replaceChildren();

      for (const value of orderedValues) {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        if (value === currentValue) {
          option.selected = true;
        }
        element.appendChild(option);
      }

      element.dataset.previousValue = element.value;
    });
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
